// 2026-07-25:WordPress Media Library API client(/api/media)。
//
// 后端 GET /api/media 返回 {items: [...], pagination: {page, per_page, total, total_pages}}
// 每条 item 是 normalized dict:
//   { id, title, filename, media_type, mime_type,
//     source_url, thumbnail_url, width, height,
//     file_size, alt_text, caption, created_at }
// 不返回 WordPress 密码 / Authorization / application_password,任何 caller 都拿不到。

import { apiFetch } from "../../../api/client";

export interface SyncMeta {
  stale: boolean;
  sync_status: string | null;
  last_synced_at: string | null;
  last_error: string | null;
  ttl_seconds: number;
}

export type MediaType = "image" | "file" | "video" | "audio";

// 2026-07-25:前端用 category(更直观的内部分类),不再直接传 WP media_type。
// 后端会把 category 映射为 WP media_type(image/video/audio)或服务端过滤(file)。
export type MediaCategory = "" | "image" | "video" | "audio" | "file";

export interface MediaItem {
  id: number;
  title: string;
  filename: string | null;
  media_type: MediaType | string;
  mime_type: string | null;
  source_url: string | null;
  thumbnail_url: string | null;
  width: number | null;
  height: number | null;
  file_size: number | null;
  alt_text: string;
  caption: string;
  created_at: string | null;
}

export interface MediaPagination {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export interface MediaListResult {
  items: MediaItem[];
  pagination: MediaPagination;
  // 2026-07-25:全量统计(不受 category / search / pagination 影响)
  library_stats?: {
    media_count: number;
    total_size_bytes: number;
    sampled: number;
  } | null;
  sync_meta?: SyncMeta;
}

export interface ListMediaParams {
  page?: number;
  per_page?: number;
  search?: string;
  category?: MediaCategory;
}

export interface UploadMediaParams {
  file: File;
  title?: string;
  alt_text?: string;
  caption?: string;
}

export interface UpdateMediaParams {
  title?: string;
  alt_text?: string;
  caption?: string;
}

/**
 * GET /api/media — 媒体列表。
 *
 * media_type="" 等价于 "all"(WP 不传 media_type 参数)。
 */
export async function listMedia(params: ListMediaParams = {}): Promise<MediaListResult> {
  const query = new URLSearchParams();
  if (params.page && params.page > 1) query.set("page", String(params.page));
  if (params.per_page && params.per_page !== 24) query.set("per_page", String(params.per_page));
  if (params.search && params.search.trim()) query.set("search", params.search.trim());
  // 2026-07-25:前端不再直接传 WP media_type(避免 file 报错),改用 category 内部分类。
  // 后端把 category 映射到 WP media_type 或服务端 mime 前缀过滤。
  if (params.category) query.set("category", params.category);

  const qs = query.toString();
  const path = qs ? `/media?${qs}` : "/media";
  return apiFetch<MediaListResult>(path);
}

export async function refreshMediaCache(force = true): Promise<{ refreshed: string[]; skipped: string[] }> {
  return apiFetch<{ refreshed: string[]; skipped: string[] }>("/media/refresh", {
    method: "POST",
    body: JSON.stringify({ force }),
  });
}

/**
 * POST /api/media/upload — multipart upload。
 *
 * 前端必须手动 fetch(不走 apiFetch),因为:
 * - apiFetch 自动设置 Content-Type: application/json(会破坏 multipart 边界)
 * - apiFetch 不知道 FormData 的 multipart 上传需要 csrf_token cookie + X-CSRF-Token header
 * - 上传需要在客户端阻断重复点击,以及处理进度(本轮不做进度条)
 *
 * 本函数手动设置 csrf header,body 用 FormData。
 */
export async function uploadMedia(params: UploadMediaParams): Promise<MediaItem> {
  const csrf = (() => {
    const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
    return m ? decodeURIComponent(m[1]) : null;
  })();
  const headers: HeadersInit = {};
  if (csrf) (headers as Record<string, string>)["X-CSRF-Token"] = csrf;

  const form = new FormData();
  form.append("file", params.file);
  if (params.title) form.append("title", params.title);
  if (params.alt_text) form.append("alt_text", params.alt_text);
  if (params.caption) form.append("caption", params.caption);

  const res = await fetch("/api/media/upload", {
    method: "POST",
    credentials: "include",
    headers,
    body: form,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    const code = json.error?.code ?? "UNKNOWN";
    const message = json.error?.message ?? `HTTP ${res.status}`;
    throw new Error(`${code}: ${message}`);
  }
  return json.data as MediaItem;
}

/**
 * (2026-07-25 撤销):之前因 WP CORS 设计了服务端 stream proxy,
 * 现已回退到"直接打开 source_url"的最小实现。
 * 此函数已无 caller,可安全删除(若未来重新引入 server proxy,可恢复)。
 */

/**
 * DELETE /api/media/<id> — 物理删除。
 */
export async function deleteMedia(id: number): Promise<{ deleted: boolean; media_id: number }> {
  return apiFetch<{ deleted: boolean; media_id: number }>(`/media/${id}`, {
    method: "DELETE",
  });
}

/**
 * PATCH /api/media/<id> — メタ更新(暂未在 UI 启用,保留 API 入口)。
 */
export async function updateMedia(id: number, body: UpdateMediaParams): Promise<MediaItem> {
  return apiFetch<MediaItem>(`/media/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}
